// Lumen Reformas · preparación de texturas FUERA del hilo principal.
// Descarga las fotos, las decodifica y calcula los mapas derivados (relieve/rugosidad, albedo aplanado, veta suavizada)
// y las texturas procedurales (yeso, hormigón, mortero, aislante). Es exactamente el mismo cálculo que hacía scene.js con
// <canvas> en el hilo principal; aquí corre en un Web Worker con OffscreenCanvas (o, si el navegador no lo admite, en el
// hilo principal como antes). Cada resultado se emite en cuanto está listo: emit(clave, fuente, volteada).

const rng = (seed) => { let s = seed >>> 0; return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; }; };

// Qué se pide: foto → salidas. «lite» (pantallas < 900 px) usa la carpeta 1k/ y mapas de relieve a la mitad.
export function plan(lite) {
  const big = lite ? 512 : 1024;
  return [
    { file: 'floor_old.webp', base: 'floorOld', derived: [{ key: 'dFloorOld', op: 'derive', size: big, rBase: 0.4, rAmp: 0.35, contrast: 1.0 }] },
    { file: 'wall_old.webp', derived: [{ key: 'wallFlat', op: 'flatten', strength: 0.92, size: 1024 }, { key: 'dWallOld', op: 'derive', size: 512, rBase: 0.85, rAmp: 0.05, contrast: 1.0 }] },
    { file: 'wood_dark.webp', derived: [{ key: 'nogal', op: 'alongGrain', size: 1024, squash: 20, keep: 0.16 }, { key: 'dWoodDark', op: 'derive', from: 'nogal', size: big, rBase: 0.46, rAmp: 0.16, contrast: 1.0 }] },
    { file: 'wood_oak.webp', base: 'woodOak', derived: [{ key: 'oakFlat', op: 'flatten', strength: 0.95, size: 1024 }, { key: 'dWoodOak', op: 'derive', from: 'oakFlat', size: big, rBase: 0.5, rAmp: 0.2, contrast: 1.4 },
      { key: 'dPlank', op: 'derive', from: 'oakFlat', size: 512, rBase: 0.3, rAmp: 0.14, contrast: 1.4 }] },
    { file: 'rug.webp', base: 'rug', derived: [{ key: 'dRug', op: 'derive', size: 512, rBase: 0.95, rAmp: 0.03, contrast: 1.0 }] },
    { file: 'view_garden.webp', base: 'garden' },
    { file: 'art_a.webp', base: 'artA' },
    { file: 'art_b.webp', base: 'artB' },
    { file: 'leaf.webp', base: 'leaf' },
  ];
}
export const PROCEDURAL = ['plaster', 'concrete', 'mortar', 'insul'];
// datos binarios que también se descargan aquí, con la misma carga «cubierta» (tablas LTC de las luces de área)
export const RAW = { ltc: 'assets/ltc.bin' };

// Carga con peticiones «cubiertas»: si pasa 0,6 s sin recibir un solo byte se lanza otra petición en paralelo (tres vivas como
// máximo; se aborta la más antigua) y gana la primera que termine. (Igual que la versión original de scene.js.)
function fetchBlob(url) {
  return new Promise((resolve, reject) => {
    let done = false, n = 0, fails = 0, lastByte = performance.now(); const live = new Set();
    const finish = (fn, v) => { if (done) return; done = true; clearInterval(watch); live.forEach(ac => ac.abort()); fn(v); };
    const attempt = () => { if (done) return; const i = n++; const ac = new AbortController(); live.add(ac); lastByte = performance.now();
      (async () => { const r = await fetch(url + (i ? `?r=${i}` : ''), { signal: ac.signal }); if (!r.ok) throw new Error('HTTP ' + r.status);
        const type = r.headers.get('content-type') || ''; const rd = r.body.getReader(); const chunks = [];
        for (;;) { const { done: end, value } = await rd.read(); if (end) break; chunks.push(value); lastByte = performance.now(); }
        return new Blob(chunks, { type }); })()
        .then(b => finish(resolve, b))
        .catch(e => { live.delete(ac); if (done) return; if (!ac.signal.aborted) fails++; if (fails >= 8) finish(reject, new Error('No se pudo cargar ' + url + ': ' + (e && e.message))); else if (live.size === 0) setTimeout(attempt, 100 * fails); }); };
    const watch = setInterval(() => { if (done || performance.now() - lastByte < 600) return; if (n >= 14) return finish(reject, new Error('No se pudo cargar ' + url + ': sin respuesta'));
      if (live.size >= 3) { const old = live.values().next().value; live.delete(old); old.abort(); } attempt(); }, 200);
    attempt();
  });
}

// como mucho 4 descargas a la vez: con HTTP/1.1 (o un servidor estático sencillo) no se encolan peticiones que el vigilante de
// arriba confundiría con conexiones colgadas; con HTTP/2 no cambia nada (el ancho de banda manda)
let slots = 4; const queue = [];
const take = () => (slots > 0 ? (slots--, Promise.resolve()) : new Promise(r => queue.push(r)));
const give = () => { const next = queue.shift(); if (next) next(); else slots++; };
const fetchLimited = (url) => take().then(() => fetchBlob(url)).finally(give);

// mk(w, h) → lienzo 2D (OffscreenCanvas en el worker, <canvas> en el hilo principal)
function ops(mk) {
  const ctx2d = (c, wrf) => c.getContext('2d', wrf ? { willReadFrequently: true } : undefined);
  // Desenfoque barato: reduce a `small` px y vuelve a ampliar con interpolación bilineal (dos pasos para suavizar)
  function blurred(srcCanvas, size, small) {
    const a = mk(small, small); const actx = ctx2d(a, true);
    actx.imageSmoothingEnabled = true; actx.imageSmoothingQuality = 'high'; actx.drawImage(srcCanvas, 0, 0, small, small);
    const m = mk(small * 8, small * 8); const mctx = ctx2d(m); mctx.imageSmoothingEnabled = true; mctx.imageSmoothingQuality = 'high'; mctx.drawImage(a, 0, 0, small * 8, small * 8);
    const b = mk(size, size); const bctx = ctx2d(b, true);
    bctx.imageSmoothingEnabled = true; bctx.imageSmoothingQuality = 'high'; bctx.drawImage(m, 0, 0, size, size);
    return bctx.getImageData(0, 0, size, size).data;
  }
  // Mapa derivado del albedo: R = altura (bump), G = rugosidad (base ± amplitud según luminancia)
  function derive(src, { size = 1024, rBase = 0.6, rAmp = 0.25, invert = false, contrast = 1.0 } = {}) {
    const c = mk(size, size); const ctx = ctx2d(c, true);
    ctx.drawImage(src, 0, 0, size, size);
    const img = ctx.getImageData(0, 0, size, size), d = img.data;
    const n = size * size;
    const bd = blurred(c, size, 32);
    const lum = new Float32Array(n);
    for (let i = 0; i < n; i++) { const l = (0.299 * d[i * 4] + 0.587 * d[i * 4 + 1] + 0.114 * d[i * 4 + 2]) / 255; lum[i] = l; }
    for (let i = 0; i < n; i++) {
      const mean = (0.299 * bd[i * 4] + 0.587 * bd[i * 4 + 1] + 0.114 * bd[i * 4 + 2]) / 255;
      let l = 0.5 + (lum[i] - mean) * contrast; l = Math.min(1, Math.max(0, l));
      const h = invert ? 1 - l : l;
      const r = Math.min(1, Math.max(0, rBase + rAmp * (0.5 - l) * 2));
      d[i * 4] = h * 255; d[i * 4 + 1] = r * 255; d[i * 4 + 2] = 128; d[i * 4 + 3] = 255;
    }
    ctx.putImageData(img, 0, 0); return c;
  }
  // Aplana la iluminación cocida en un albedo generado por IA (divide por su propia versión desenfocada)
  function flatten(src, { strength = 0.85, size = 2048 } = {}) {
    const c = mk(size, size); const ctx = ctx2d(c, true);
    ctx.drawImage(src, 0, 0, size, size);
    const bd = blurred(c, size, 24); const n = size * size;
    let gm = 0; for (let i = 0; i < n; i++) gm += (0.299 * bd[i * 4] + 0.587 * bd[i * 4 + 1] + 0.114 * bd[i * 4 + 2]); gm /= n;
    const img = ctx.getImageData(0, 0, size, size), d = img.data;
    for (let i = 0; i < n; i++) {
      const bl = Math.max(8, 0.299 * bd[i * 4] + 0.587 * bd[i * 4 + 1] + 0.114 * bd[i * 4 + 2]); const k = Math.pow(gm / bl, strength); const j = i * 4;
      d[j] = Math.min(255, d[j] * k); d[j + 1] = Math.min(255, d[j + 1] * k); d[j + 2] = Math.min(255, d[j + 2] * k); }
    ctx.putImageData(img, 0, 0); return c;
  }
  // Nogal: desenfoque SOLO a lo largo de la veta (eje V) con un poco del original; de paso comprime el contraste.
  function alongGrain(src, { size = 1024, squash = 20, keep = 0.16 } = {}) {
    const c = mk(size, size); const ctx = ctx2d(c); ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
    const a = mk(size, squash); ctx2d(a).drawImage(src, 0, 0, size, squash);
    const b = mk(size, squash * 6); const bctx = ctx2d(b); bctx.imageSmoothingQuality = 'high'; bctx.drawImage(a, 0, 0, size, squash * 6);
    ctx.drawImage(b, 0, 0, size, size); ctx.globalAlpha = keep; ctx.drawImage(src, 0, 0, size, size); ctx.globalAlpha = 1; return c;
  }
  function noiseTex(size, base, amp, tint = [1, 1, 1], grid = null) {
    const c = mk(size, size); const ctx = ctx2d(c);
    const img = ctx.createImageData(size, size), d = img.data, r = rng(7);
    for (let i = 0; i < size * size; i++) {
      const v = base + (r() - 0.5) * amp * 2;
      d[i * 4] = v * 255 * tint[0]; d[i * 4 + 1] = v * 255 * tint[1]; d[i * 4 + 2] = v * 255 * tint[2]; d[i * 4 + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
    for (let k = 0; k < 40; k++) {
      const g = ctx.createRadialGradient(r() * size, r() * size, 0, r() * size, r() * size, size * (0.1 + r() * 0.25));
      g.addColorStop(0, `rgba(0,0,0,${0.05 + r() * 0.08})`); g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g; ctx.fillRect(0, 0, size, size);
    }
    if (grid && grid.dots) { const n = grid.dots, st = size / n; for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) { const cx = (i + 0.5) * st, cy = (j + 0.5) * st; const g = ctx.createRadialGradient(cx - st * 0.08, cy - st * 0.08, 0, cx, cy, st * 0.34); g.addColorStop(0, 'rgba(255,255,255,0.55)'); g.addColorStop(0.7, 'rgba(120,135,150,0.35)'); g.addColorStop(1, 'rgba(60,70,80,0)'); ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, st * 0.34, 0, 6.2832); ctx.fill(); } }
    if (grid) { ctx.strokeStyle = grid.color; ctx.lineWidth = grid.w; for (let x = 0; x <= size; x += size / grid.nx) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, size); ctx.stroke(); } for (let y = 0; y <= size; y += size / grid.ny) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(size, y); ctx.stroke(); } }
    return c;
  }
  function plasterTex(size = 512) {
    const r = rng(91); const layer = (cells) => { const g = new Float32Array(cells * cells); for (let i = 0; i < g.length; i++) g[i] = r(); return (u, v) => { const x = u * cells, y = v * cells; const x0 = Math.floor(x), y0 = Math.floor(y); const fx = x - x0, fy = y - y0; const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
        const a = g[(y0 % cells) * cells + (x0 % cells)], b = g[(y0 % cells) * cells + ((x0 + 1) % cells)], c = g[((y0 + 1) % cells) * cells + (x0 % cells)], d = g[((y0 + 1) % cells) * cells + ((x0 + 1) % cells)]; return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy; }; };
    const fine = [layer(160), layer(72), layer(28)], low = [layer(3), layer(7), layer(15)];
    const c = mk(size, size); const ctx = ctx2d(c); const img = ctx.createImageData(size, size), d = img.data;
    for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) { const u = x / size, v = y / size, i = (y * size + x) * 4;
      const h = fine[0](u, v) * 0.5 + fine[1](u, v) * 0.32 + fine[2](u, v) * 0.18, t = low[0](u, v) * 0.5 + low[1](u, v) * 0.3 + low[2](u, v) * 0.2;
      d[i] = h * 255; d[i + 1] = t * 255; d[i + 2] = 128; d[i + 3] = 255; }
    ctx.putImageData(img, 0, 0); return c;
  }
  return { derive, flatten, alongGrain, noiseTex, plasterTex };
}

// Ejecuta todo el plan. finish(canvas|bitmap, esFoto) → fuente lista para la GPU; emit(clave, fuente) la entrega;
// loaded(file) avisa de cada descarga terminada (para el preloader).
export async function runJobs({ base, root, lite, mk, finish, emit, loaded, fail }) {
  const O = ops(mk);
  const raws = Object.entries(RAW).map(([key, url]) => fetchLimited(root + url).then(b => b.arrayBuffer()).then(buf => emit(key, buf)).catch(e => fail(String(e && e.message || e))));
  const rest = () => new Promise(r => setTimeout(r, 0));
  const jobs = plan(lite).map(async (j) => {
    try {
      const blob = await fetchLimited(base + j.file); loaded(j.file);
      const src = await createImageBitmap(blob, { premultiplyAlpha: 'none', colorSpaceConversion: 'none' });
      const made = {};
      for (const d of j.derived || []) {
        await rest();
        const from = d.from ? made[d.from] : src;
        made[d.key] = d.op === 'derive' ? O.derive(from, d) : d.op === 'flatten' ? O.flatten(from, d) : O.alongGrain(from, d);
      }
      for (const d of j.derived || []) emit(d.key, await finish(made[d.key], false));
      if (j.base) emit(j.base, await finish(src, true)); else if (src.close) src.close();
    } catch (e) { fail(String(e && e.message || e)); }
  });
  await rest();
  emit('plaster', await finish(O.plasterTex(512), false)); await rest();
  emit('concrete', await finish(O.noiseTex(512, 0.52, 0.08, [1, 0.98, 0.95]), false)); await rest();
  emit('mortar', await finish(O.noiseTex(512, 0.58, 0.05, [1, 0.99, 0.97]), false)); await rest();
  emit('insul', await finish(O.noiseTex(512, 0.86, 0.03, [0.97, 0.98, 1], { color: 'rgba(70,82,95,0.7)', w: 2, nx: 4, ny: 4, dots: 20 }), false));
  await Promise.all([...jobs, ...raws]);
}
